import {
  Table,
  TableHeader,
  TableRow,
  TableHead,
  TableBody,
  TableCell,
} from "@/components/ui/table";

import {
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import { createColumns } from "../../columns/alterantiveProductTableColumn";
import { cn } from "@/libs/utils";
import { useMemo, useState } from "react";
import { useSetExpandedRowId } from "../../hooks/useTableStore";
import {
  CombinedRow,
  GoogleSearchResult,
  PurchaseHistory,
} from "@/types/itemsheet";
import { useFormContext, useFieldArray } from "react-hook-form";

type Props = {
  id: string; // 상위 행의 id
  index: number; // 상위 행의 index
  googleSearchResult: GoogleSearchResult[];
  purchaseHistory: PurchaseHistory[];
};

export default function EstimateAlternativeProductTable({
  id,
  index,
  googleSearchResult,
  purchaseHistory,
}: Props) {
  const { setValue, getValues, trigger, control } = useFormContext<{
    rows: CombinedRow[];
  }>();
  const { fields, update } = useFieldArray({
    control,
    name: "rows",
  });
  const setExpandedRowId = useSetExpandedRowId();
  const [forceUpdate, setForceUpdate] = useState(0);

  const handleRowSelect = async (
    rowId: string,
    list_price?: string | number | null,
    unit_cost?: string | number | null
  ) => {
    const row = table.getRowModel().rows.find((row) => row.id === rowId);

    if (row) {
      const formValues = getValues();
      const currentRow = formValues.rows[index];
      const newRow = row.original;

      // Convert list_price and unit_cost to number or null
      const parsedListPrice = list_price ? Number(list_price) : null;
      const parsedUnitCost = unit_cost ? Number(unit_cost) : null;

      // 판매가와 매입가 처리
      const finalListPrice = parsedListPrice || newRow.list_price;
      const finalUnitCost = parsedUnitCost || newRow.unit_cost;

      // 현재 행의 수량을 유지 (alternative 데이터에는 수량이 없을 수 있음)
      const finalQuantity = newRow.quantity || currentRow.quantity;

      // 원본 행의 중요 메타데이터 보존 (태그, 아이콘 등)
      const updateData = {
        ...currentRow, // 먼저 현재 행의 모든 데이터 유지
        product_name: newRow.product_name || currentRow.product_name,
        product_id: newRow.product_id || currentRow.product_id,
        manufacturer: newRow.manufacturer || currentRow.manufacturer,
        specification: newRow.specification || currentRow.specification,
        unit: newRow.unit || currentRow.unit,
        vendor: newRow.vendor || currentRow.vendor,
        buyer: newRow.buyer || currentRow.buyer,
        quantity: finalQuantity,
        list_price: finalListPrice,
        unit_cost: finalUnitCost,
        // 태그와 아이콘 관련 데이터는 currentRow에서 유지
      };

      // useFieldArray의 update 함수 사용
      update(index, updateData);

      // 폼 유효성 검사 트리거 (리렌더링 유도)
      await trigger(`rows.${index}.list_price`);

      setExpandedRowId(id, false); // 선택 후 상위 행을 닫음
    } else {
      console.error("❌ Row not found for rowId:", rowId);
    }
  };

  const rows: CombinedRow[] = useMemo(() => {
    const googleRows = googleSearchResult.map((item) => {
      const pricing = item.pricing_table?.[0];
      const unit = pricing?.unit ?? "";
      const rawPrice = pricing?.price_per_unit?.trim() ?? "";
      const intVal = parseInt(rawPrice.replace(/[^0-9.]/g, ""));

      const finalDisplayPrice = intVal ? intVal : null;

      return {
        ...({
          uuid: null,
          seq_no: null,
          product_name: item.product_name ?? "",
          product_id: item.item_no ?? "",
          manufacturer: item.manufacturer ?? "",
          vendor: item.vendor ?? "",
          buyer: "",
          specification: item.specification ?? "",
          unit,
          quantity: null,
          unit_cost: null,
          list_price: finalDisplayPrice,
          total_cost: null,
          total_list_price: null,
          extra: {},
          list_num: null,
          tags: [],
          googleSearchResult: [],
          purchaseHistory: [],
          isGoogleResult: true,
        } satisfies CombinedRow),
        url: item.url ?? "",
      };
    });

    const purchaseRows: CombinedRow[] = purchaseHistory.map((item) => ({
      uuid: null,
      seq_no: null,
      product_name: item.item_name ?? "",
      product_id: item.item_no ?? "",
      manufacturer: item.manufacturer ?? "",
      vendor: item.vendor ?? "",
      buyer: "",
      specification: item.spec ?? "",
      unit: "",
      quantity: null,
      unit_cost: null,
      list_price: item.unit_cost ?? null,
      total_cost: null,
      total_list_price: null,
      extra: {},
      list_num: null,
      tags: [],
      googleSearchResult: [],
      purchaseHistory: [],
      isPurchaseHistory: true,
    }));

    return [...googleRows, ...purchaseRows];
  }, [googleSearchResult, purchaseHistory]);

  const columns = useMemo(() => createColumns(handleRowSelect), []);

  const tableData = useMemo(() => [...rows], [rows]);

  const table = useReactTable({
    data: tableData,
    columns,
    getCoreRowModel: getCoreRowModel(),
    meta: {
      updateData: (rowIndex: number, columnId: string, value: unknown) => {
        // Force a table rerender
        setForceUpdate((prev: number) => prev + 1);
      },
    },
  });

  return (
    <div className="rounded-md shadow-xl relative">
      <Table>
        <TableHeader>
          {table.getHeaderGroups().map((headerGroup) => (
            <TableRow key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                return (
                  <TableHead
                    key={header.id}
                    className="h-9 px-2 text-sm text-white bg-gray-A"
                    style={{
                      minWidth: header.column.columnDef.size,
                      maxWidth: header.column.columnDef.size,
                    }}
                  >
                    {header.isPlaceholder
                      ? null
                      : flexRender(
                          header.column.columnDef.header,
                          header.getContext()
                        )}
                  </TableHead>
                );
              })}
            </TableRow>
          ))}
        </TableHeader>
        <TableBody>
          {table.getRowModel().rows?.length ? (
            table.getRowModel().rows.map((row) => (
              <TableRow
                key={row.id}
                data-state={row.getIsSelected() && "selected"}
                className={cn(row.getIsSelected() && "bg-green/20", "group")}
              >
                {row.getVisibleCells().map((cell) => (
                  <TableCell
                    key={cell.id}
                    style={{
                      minWidth: cell.column.columnDef.size,
                      maxWidth: cell.column.columnDef.size,
                    }}
                    className="h-9 p-2"
                  >
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </TableCell>
                ))}
              </TableRow>
            ))
          ) : (
            <TableRow>
              <TableCell colSpan={columns.length} className="h-24 text-center">
                검색 결과가 없습니다.
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </div>
  );
}
